import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { LayoutDashboard, UserCheck, Clock, CheckCircle2, FileSearch, TrendingUp, AlertTriangle, Bell, BellRing, FileText, Archive, UploadCloud, Send, SlidersHorizontal, LogOut, Menu, X, ShieldOff, Activity, MessageSquareText, Bot } from "lucide-react";
import { api, API_BASE, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { EmailHealthBanner } from "@/components/common/EmailHealthBanner";

export const NAV = [
  { id: "agency-overview", label: "Agency Overview", to: "/", icon: LayoutDashboard, end: true },
  { id: "employee-scorecards", label: "Employee Scorecards", to: "/scorecards", icon: UserCheck },
  { id: "open-tasks", label: "Open Tasks", to: "/tasks/open", icon: Clock },
  { id: "closed-tasks", label: "Closed Tasks", to: "/tasks/closed", icon: CheckCircle2 },
  { id: "due-date-audit", label: "Due-Date Audit", to: "/due-date-audit", icon: FileSearch },
  { id: "new-business", label: "New Business", to: "/new-business", icon: TrendingUp },
  { id: "cancellation-watch", label: "Cancellation Watch", to: "/cancellations", icon: AlertTriangle },
  { id: "alerts", label: "Alerts", to: "/alerts", icon: Bell },
  { id: "alert-rules", label: "Alert Rules", to: "/alert-rules", icon: BellRing, manager: true },
  { id: "workforce", label: "Workforce Activity", to: "/workforce", icon: Activity, admin: true },
  { id: "ask", label: "Ask TQI", to: "/ask", icon: MessageSquareText, admin: true },
  { id: "bot-control", label: "Bot Control Center", to: "/bot-control", icon: Bot, bc: true },
  { id: "report-builder", label: "Report Builder", to: "/reports/builder", icon: FileText, admin: true },
  { id: "report-archive", label: "Report Archive", to: "/reports/archive", icon: Archive, admin: true },
  { id: "imports", label: "Imports", to: "/imports", icon: UploadCloud, admin: true },
  { id: "delivery-monitor", label: "Delivery Monitor", to: "/delivery", icon: Send, admin: true },
  { id: "settings", label: "Settings", to: "/settings", icon: SlidersHorizontal, admin: true },
];

export function BrandMark({ light = false, size = "md" }) {
  const [broken, setBroken] = useState(false);
  const { data } = useQuery({ queryKey: ["branding"], queryFn: () => api.get("/branding").then((r) => r.data), staleTime: 300000 });
  if (data?.has_logo && !broken) {
    return <img src={`${API_BASE}/branding/logo?v=${data.logo_version}`} alt="Total Quality Insurance" onLoad={(e) => { if (e.target.naturalWidth < 8) setBroken(true); }} onError={() => setBroken(true)} className={cn("object-contain", size === "lg" ? "h-14" : "h-9")} data-testid="brand-logo" />;
  }
  return (
    <div className="flex items-center gap-2" data-testid="brand-wordmark">
      <span className={cn("grid place-items-center rounded font-extrabold tracking-tight", size === "lg" ? "h-12 w-12 text-xl" : "h-9 w-9 text-sm", light ? "bg-white text-[#101C47]" : "bg-[#101C47] text-white")}>TQI</span>
      <div className="leading-tight">
        <div className={cn("font-extrabold tracking-tight", size === "lg" ? "text-xl" : "text-sm", light ? "text-white" : "text-[#101C47]")}>Operations Hub</div>
        <div className={cn("text-[10px] uppercase tracking-widest", light ? "text-white/60" : "text-slate-500")}>Total Quality Insurance</div>
      </div>
    </div>
  );
}

function FeedPulse() {
  const { data } = useQuery({ queryKey: ["freshness"], queryFn: () => api.get("/overview/freshness").then((r) => r.data), refetchInterval: 60000 });
  if (!data) return null;
  const fresh = data.feeds.filter((f) => f.status === "fresh").length;
  const total = data.feeds.length;
  const tone = fresh === total ? "bg-emerald-50 text-emerald-700 border-emerald-200" : fresh === 0 ? "bg-slate-100 text-slate-600 border-slate-300" : "bg-amber-50 text-amber-800 border-amber-300";
  return <span className={cn("hidden sm:inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold", tone)} data-testid="feed-heartbeat"><span className="h-2 w-2 rounded-full bg-current" />{fresh}/{total} feeds fresh</span>;
}

function SystemPulse({ role }) {
  const { data } = useQuery({ queryKey: ["health-engine"], queryFn: () => api.get("/system/health-engine").then((r) => r.data), refetchInterval: 60000, enabled: role !== "employee" });
  if (!data || !data.checked_at) return null;
  const word = { ok: "All systems working", degraded: "Something needs attention", down: "A system is down" }[data.overall] || "Health unknown";
  const tone = { ok: "bg-emerald-50 text-emerald-800 border-emerald-200", degraded: "bg-amber-50 text-amber-900 border-amber-200", down: "bg-rose-50 text-rose-800 border-rose-200" }[data.overall] || "bg-slate-100 text-slate-600 border-slate-300";
  const dot = { ok: "bg-emerald-500", degraded: "bg-amber-500", down: "bg-rose-600 animate-pulse" }[data.overall] || "bg-slate-400";
  return <Link to="/settings/system" className={cn("hidden md:inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold transition-colors hover:brightness-95", tone)} data-testid="system-pulse" title="Open System Health"><span className={cn("h-2 w-2 rounded-full", dot)} />{word}</Link>;
}

const GROUPS = [["Overview & Activity", ["agency-overview", "employee-scorecards", "workforce", "alerts"]], ["Tasks & Policies", ["open-tasks", "closed-tasks", "due-date-audit", "new-business", "cancellation-watch"]], ["Automation & Intelligence", ["ask", "bot-control", "alert-rules", "report-builder", "report-archive"]], ["Admin", ["imports", "delivery-monitor", "settings"]]];

export function AppShell({ children }) {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const items = NAV.filter((n) => (!n.admin || user.role === "admin") && (!n.manager || user.role !== "employee") && (!n.bc || (user.bot_control_role && user.bot_control_role !== "none")));

  const signOut = async () => {
    await logout();
    navigate("/login");
  };

  const signOutEverywhere = async () => {
    if (!window.confirm("Sign out of every device and browser where you are signed in?")) return;
    try {
      const r = await api.post("/auth/logout-all");
      toast.success(`Signed out everywhere (${r.data.sessions_revoked} session(s))`);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setUser(false);
    navigate("/login");
  };

  const sidebar = (
    <aside className="flex h-full w-72 flex-col bg-[#101C47] text-white tqi-grain" data-testid="sidebar">
      <div className="flex items-center justify-between px-5 py-5 border-b border-white/10">
        <BrandMark light />
        <button className="lg:hidden text-white/70" onClick={() => setOpen(false)} aria-label="Close menu" data-testid="sidebar-close-button"><X className="h-5 w-5" /></button>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-2" data-testid="sidebar-nav">
        {GROUPS.map(([group, ids]) => {
          const grouped = items.filter((n) => ids.includes(n.id));
          if (!grouped.length) return null;
          return (
            <div key={group}>
              <div className="tqi-nav-group">{group}</div>
              {grouped.map(({ id, label, to, icon: Icon, end }) => (
                <NavLink key={id} to={to} end={end} onClick={() => setOpen(false)} data-testid={`nav-item-${id}`}
                  className={({ isActive }) => cn("flex items-center gap-3 rounded-md border-l-[3px] px-3 py-2 text-sm transition-colors duration-150", isActive ? "border-[#F92626] bg-white/10 font-semibold text-white" : "border-transparent text-slate-300 hover:bg-white/5 hover:text-white")}>
                  <Icon className="h-4 w-4 shrink-0 opacity-90" />{label}
                </NavLink>
              ))}
            </div>
          );
        })}
        {items.filter((n) => !GROUPS.some(([, ids]) => ids.includes(n.id))).map(({ id, label, to, icon: Icon, end }) => (
          <NavLink key={id} to={to} end={end} onClick={() => setOpen(false)} data-testid={`nav-item-${id}`} className={({ isActive }) => cn("flex items-center gap-3 rounded-md border-l-[3px] px-3 py-2 text-sm", isActive ? "border-[#F92626] bg-white/10 font-semibold text-white" : "border-transparent text-slate-300 hover:bg-white/5 hover:text-white")}><Icon className="h-4 w-4 shrink-0" />{label}</NavLink>
        ))}
      </nav>
      <div className="border-t border-white/10 px-5 py-4 text-xs text-white/60">
        <div className="font-semibold text-white/90 truncate" data-testid="sidebar-user-email">{user.email}</div>
        <div className="uppercase tracking-wider mt-0.5" data-testid="sidebar-user-role">{user.role}</div>
      </div>
    </aside>
  );

  return (
    <div className="flex min-h-screen">
      <div className="hidden lg:block fixed inset-y-0 left-0 z-40">{sidebar}</div>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="h-full">{sidebar}</div>
          <div className="flex-1 bg-black/40" onClick={() => setOpen(false)} />
        </div>
      )}
      <div className="flex-1 lg:pl-72 flex flex-col min-w-0">
        <header className="h-16 sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200 px-4 sm:px-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-[#101C47]" onClick={() => setOpen(true)} aria-label="Open menu" data-testid="sidebar-open-button"><Menu className="h-5 w-5" /></button>
            <FeedPulse />
            <SystemPulse role={user.role} />
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-1.5 py-1 pr-3 text-xs shadow-sm" data-testid="header-user-pill">
              <span className="grid h-6 w-6 place-items-center rounded-full bg-[#101C47] text-[10px] font-bold text-white">{(user.name || user.email).slice(0, 2).toUpperCase()}</span>
              <span className="font-semibold text-slate-800">{user.name || user.email}</span>
              <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-mono font-bold uppercase tracking-wider text-slate-600">{user.role}</span>
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button variant="outline" size="sm" data-testid="logout-menu-button" className="gap-2"><LogOut className="h-4 w-4" />Sign out</Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end" data-testid="logout-menu">
                <DropdownMenuItem onClick={signOut} data-testid="logout-button"><LogOut className="mr-2 h-4 w-4" />Sign out this browser</DropdownMenuItem>
                <DropdownMenuItem onClick={signOutEverywhere} className="text-[#F92626] focus:text-[#F92626]" data-testid="logout-everywhere-button"><ShieldOff className="mr-2 h-4 w-4" />Sign out everywhere</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <EmailHealthBanner role={user.role} />
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1600px] w-full">{children}</main>
      </div>
    </div>
  );
}
